'use client'

import { useCallback, useMemo, useState } from 'react'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import type { AuctionUpdates } from '@b-stock/fe-core/bstock-next/helpers'
import {
  use3MPAuctionUpdates,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import {
  NestableAnalyticsProvider,
  interpUri,
} from '@b-stock/fe-core/bstock-react'

import { hasUnacceptedTermsRestrictions } from '@/components/listings/ListView/listingHelpers'
import {
  makeSearchDataUpdater,
  useListingSearch,
} from '@/components/listings/search/ListingSearchProvider'
import { apply3MPAuctionUpdate } from '@/components/listings/shared/auctionUpdateUtils'
import { useEnterpriseAuctionUpdates } from '@/components/listings/shared/useEnterpriseAuctionUpdates'
import ComponentErrorBoundary from '@/helpers/ComponentErrorBoundary'
import {
  FeatureFlags,
  type ManifestSummarizeLabelVariant,
} from '@/helpers/featureFlags'
import { toBidderCountSegmentProps } from '@/helpers/listing/bidderCount'
import { getEndTime } from '@/helpers/listing/getEndTime'
import { useGridCardPrice } from '@/helpers/listing/useGridCardPrice'
import { getPromotedAnalyticsFields } from '@/helpers/telemetry/promotedListingAnalytics'
import type { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useDealBadgeData } from '@/hooks/useDealBadgeData'
import acceptedPoliciesQuery from '@/queries/acceptedPoliciesQuery'
import type { SearchResult } from '@/queries/listingSearchQuery'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'

import ManifestSummaryPopup from '../ManifestSummaryPopup'
import BaseListingCard from './BaseListingCard'
import { useCardFirstDibsEarlyPrice } from './BaseListingCard/ListingInfo/useCardFirstDibsWindow'

/**
 * Please Read:
 *
 * The exported component here is designed to handle both 3MP and enterprise
 * listings. There's kind of a diamond pattern going on here, where depending
 * on the listing's origin, we render a variant component that understands
 * how to watch for updates from the appropriate source, but both variants
 * should render the same BaseListingCard component. BaseListingCard should
 * be purely presentational and not have to account for 3MP/ent differences.
 */

type ListingCardProps = {
  listing: SearchResult
  screenName: ScreenName
  position: number
}

type ListingCardProps3MP = ListingCardProps & {
  auction: NonNullable<SearchResult['auction3MP']>
  hasTermsRestrictions: boolean
  onSummarizeClick?: () => void
  summarizeLabelVariant: ManifestSummarizeLabelVariant
}

type ListingCardPropsEnt = ListingCardProps & {
  auction: SearchResult['auctionEnt']
  hasTermsRestrictions: boolean
  onSummarizeClick?: () => void
  summarizeLabelVariant: ManifestSummarizeLabelVariant
}

const ListingCard = ({ listing, screenName, position }: ListingCardProps) => {
  const auction3MP = listing.auction3MP
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const summarizeVariant = useFlag(FeatureFlags.ENABLE_ALP_MANIFEST_SUMMARIZE)
  const isSummarizeEnabled = summarizeVariant !== 'off'
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })
  const { data: acceptedPolicies } = acceptedPoliciesQuery.useQuery()

  const summarizeLabelVariant: ManifestSummarizeLabelVariant =
    summarizeVariant !== 'off' ? summarizeVariant : 'summarize'

  // Gated on the buyer's accepted policies, not merely on the terms existing:
  // otherwise a no-bid auction (winningBidAmount stays 0) kept hiding its price
  // behind "View details" even after the buyer accepted that seller's terms.
  const hasTermsRestrictions =
    hasUnacceptedTermsRestrictions(listing, acceptedPolicies ?? null) &&
    !listing.winningBidAmount
  const isNewSellerListing =
    isNewSellerTreatment && (newSellerIds?.has(listing.storefrontId) ?? false)

  const { segmentProps: dealBadgeSegmentProps } = useDealBadgeData(
    listing,
    screenName
  )

  const analyticsData = useMemo(
    () => ({
      listing_id: listing.listingId,
      storefront_name: listing.storefrontName,
      storefront_id: listing.storefrontId,
      ...dealBadgeSegmentProps,
      ...toBidderCountSegmentProps(listing),
      is_new_seller: isNewSellerListing,
      ...getPromotedAnalyticsFields(listing),
    }),
    [listing, dealBadgeSegmentProps, isNewSellerListing]
  )

  const handleSummarizeClick = useCallback(() => {
    setIsPopupOpen(true)
  }, [])

  const handlePopupClose = useCallback(() => {
    setIsPopupOpen(false)
  }, [])

  return (
    <NestableAnalyticsProvider value={analyticsData}>
      {isPopupOpen && (
        <ManifestSummaryPopup listing={listing} onClose={handlePopupClose} />
      )}
      <ComponentErrorBoundary>
        {auction3MP ? (
          <ListingCard3MP
            listing={listing}
            auction={auction3MP}
            screenName={screenName}
            hasTermsRestrictions={hasTermsRestrictions}
            position={position}
            onSummarizeClick={
              isSummarizeEnabled ? handleSummarizeClick : undefined
            }
            summarizeLabelVariant={summarizeLabelVariant}
          />
        ) : (
          <ListingCardEnt
            listing={listing}
            auction={listing.auctionEnt}
            screenName={screenName}
            hasTermsRestrictions={hasTermsRestrictions}
            position={position}
            onSummarizeClick={
              isSummarizeEnabled ? handleSummarizeClick : undefined
            }
            summarizeLabelVariant={summarizeLabelVariant}
          />
        )}
      </ComponentErrorBoundary>
    </NestableAnalyticsProvider>
  )
}

const ListingCard3MP = ({
  listing,
  auction,
  screenName,
  hasTermsRestrictions,
  position,
  onSummarizeClick,
  summarizeLabelVariant,
}: ListingCardProps3MP) => {
  const { updateSearchData, pusherClient3MP } = useListingSearch()
  const { accountId } = useDecodedAccessToken() ?? { accountId: null }

  const updateAuctionData = useCallback(
    (update: AuctionUpdates) => {
      let newBidStatus = BidStatus.NO_BID

      updateSearchData(
        makeSearchDataUpdater(listing.listingId, (listing) => {
          const { updatedListing, bidStatus } = apply3MPAuctionUpdate(
            listing,
            update
          )
          newBidStatus = bidStatus
          return updatedListing
        })
      )
      return { bidStatus: newBidStatus }
    },
    [updateSearchData, listing.listingId]
  )

  use3MPAuctionUpdates({
    listingId: auction._id,
    viewerAccountId: accountId,
    auction,
    pusherClient: pusherClient3MP,
    updateAuctionData,
  })

  const gridCardPrice = useGridCardPrice(listing, auction)
  // While the buyer's First Dibs window is open, the LDP sells at the group's
  // early price, so the card shows that price (and its % MSRP / per unit).
  const fdEarlyPrice = useCardFirstDibsEarlyPrice(listing)
  const currentPrice = fdEarlyPrice ?? gridCardPrice

  return (
    <BaseListingCard
      listing={listing}
      listingDetailsUrl={interpUri`/buy/listings/details/${auction.listingId}`}
      currentPrice={currentPrice}
      isFdBuyNow={fdEarlyPrice !== undefined}
      bidStatus={auction.bidStatus}
      bidAmount={auction.bid?.bidAmount}
      endTime={getEndTime(listing)}
      screenName={screenName}
      hasTermsRestrictions={hasTermsRestrictions}
      position={position}
      onSummarizeClick={onSummarizeClick}
      summarizeLabelVariant={summarizeLabelVariant}
    />
  )
}

const ListingCardEnt = ({
  listing,
  auction,
  screenName,
  hasTermsRestrictions,
  position,
  onSummarizeClick,
  summarizeLabelVariant,
}: ListingCardPropsEnt) => {
  const { updateSearchData, pusherClientEnt } = useListingSearch()

  // Both of these are dependencies of the subscription effect inside
  // useEnterpriseAuctionUpdates, so passing a fresh array / arrow each render
  // would tear down and re-subscribe the listing's Pusher channel on every
  // render of this card.
  const entListings = useMemo(() => [listing], [listing])
  const handleListingUpdate = useCallback(
    (listingId: string, updatedListing: SearchResult) => {
      updateSearchData(makeSearchDataUpdater(listingId, () => updatedListing))
    },
    [updateSearchData]
  )

  useEnterpriseAuctionUpdates({
    listings: entListings,
    pusherClientEnt,
    onListingUpdate: handleListingUpdate,
  })

  // `|| 0` matches getGridCardPrice (the 3MP path): winningBidAmount is
  // optional on the search doc, and Number(undefined) is NaN.
  const currentPrice =
    auction?.winningBidAmount ||
    auction?.startPrice ||
    Number(listing.winningBidAmount) ||
    0

  return (
    <BaseListingCard
      listing={listing}
      listingDetailsUrl={listing.auctionUrl}
      currentPrice={currentPrice}
      bidStatus={auction?.bidStatus ?? BidStatus.NO_BID}
      endTime={getEndTime(listing)}
      screenName={screenName}
      hasTermsRestrictions={hasTermsRestrictions}
      position={position}
      onSummarizeClick={onSummarizeClick}
      summarizeLabelVariant={summarizeLabelVariant}
    />
  )
}

export default ListingCard
